import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Credit, Job, actions } from '@kanda-libs/ks-frontend-services';

import { useAppDispatch } from 'components/App';
import useApiError from 'hooks/useApiError';
import { useStatusContext } from 'pages/Status/context';

import { HummLenderStatus, getHummState } from './helpers';

export interface HummStatusHook {
  state: HummLenderStatus;
}

export default function useHummStatus(): HummStatusHook {
  const [isChecking, setIsChecking] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [iterations, setIterations] = useState<number>(0);
  const { search } = useLocation();

  const success = useMemo(() => {
    if (!search) return false;
    const params = new URLSearchParams(search);
    return Boolean(params.get('success'));
  }, [search]);

  const { job, credit, deposit, isLoading } = useStatusContext();
  const dispatch = useAppDispatch();
  const onCheckError = useApiError('Error checking credit - please refresh');
  const onJobError = useApiError('Error checking job - please refresh');

  const state = useMemo(() => {
    if (isLoading || !job || !credit) return 'loading';
    if (isChecking) return 'checking';
    if (error) return 'error';
    return getHummState(job, credit, deposit);
  }, [isChecking, isLoading, job, credit, deposit, error]);

  const successTimerRef = useRef<NodeJS.Timer | null>(null);
  const checkSuccessData = useCallback(
    (checkedCredit: Credit, refreshedJob: Job, callback: () => void) => {
      const jobStatus = refreshedJob.status;
      const financeStatus = refreshedJob?.finance_status;
      const creditState = checkedCredit?.state;
      if (financeStatus === 'financed' && creditState === 'accepted') {
        setIsChecking(false);
        return;
      }
      if (
        !success &&
        jobStatus === 'sent' &&
        financeStatus === 'applied_for_finance'
      ) {
        setIsChecking(false);
        return;
      }
      successTimerRef.current = setTimeout(() => {
        callback();
      }, 5000);
    },
    [success],
  );

  const checkCreditCallback = useCallback(
    (creditId: string, jobId: string) => {
      setIterations((i) => i + 1);
      dispatch(
        actions.checkCredit({
          params: { id: creditId },
          onSuccess: (checkedCredit: Credit) => {
            dispatch(
              actions.getJob({
                params: { id: jobId },
                forceReload: true,
                onSuccess: (refreshedJob: Job) => {
                  checkSuccessData(checkedCredit, refreshedJob, () =>
                    checkCreditCallback(creditId, jobId),
                  );
                },
                onError: (err) => {
                  onJobError(err);
                  setError(true);
                  setIsChecking(false);
                },
              }),
            );
          },
          onError: (err) => {
            onCheckError(err);
            setError(true);
            setIsChecking(false);
          },
        }),
      );
    },
    [dispatch, checkSuccessData, onCheckError, onJobError],
  );

  const checkRef = useRef<boolean>(false);
  useEffect(() => {
    if (isLoading || !credit || !job || checkRef.current) return;
    const creditId = credit?.id;
    const jobId = job?.id;
    checkRef.current = true;
    if (!creditId || !jobId) {
      setIsChecking(false);
      return;
    }
    const financeStatus = job?.finance_status;
    const creditState = credit?.state;
    if (financeStatus !== 'applied_for_finance') {
      setIsChecking(false);
      return;
    }
    if (!success && creditState === 'referred') {
      setIsChecking(false);
      return;
    }
    if (
      success &&
      !(creditState === 'referred' || creditState === 'accepted')
    ) {
      setIsChecking(false);
      return;
    }
    checkCreditCallback(creditId, jobId);
  }, [checkCreditCallback, success, credit, job, isLoading]);

  useEffect(() => {
    if (iterations >= 24) {
      setError(true);
      setIsChecking(false);
      clearTimeout(successTimerRef.current as unknown as number);
    }
  }, [iterations]);

  return {
    state,
  };
}
